class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    // helper variables
    this.cartTotalValue = 0;
    this.cartTotalDiscount = 0;
    this.cartOriginalPrice = 0;

    // elements
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    // events
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // Init items from API to prevent cart content missmatch on browser back/forward commands
    this.renderItems();

    // dev events
    document.addEventListener('ajaxProduct:added', (event) => {
      const header = document.getElementById('MainHeader');
      const behavior = header ? header.getAttribute('data-cart-open-behavior') : 'always';

      const storageKey = 'cart_drawer_opened_once';
      const openedOnce = window.sessionStorage.getItem(storageKey) === 'true';
      const willOpen = behavior === 'always' || (behavior === 'first_time' && !openedOnce);

      if (willOpen) {
        this.renderAndShowItems();
        if (behavior === 'first_time') {
          window.sessionStorage.setItem(storageKey, 'true');
        }
      } else {
        // Update sections silently when drawer should not open
        this.renderItems();

        // Ensure blip is visible, then pulse with a slight delay so first-time works reliably
        const blip = document.querySelector('.cart-blip');
        if (blip) {
          blip.removeAttribute('hidden');
          const blipLink = blip.closest('.header-item__link');
          if (blipLink) blipLink.classList.add('has-blip-visible');

          requestAnimationFrame(() => {
            setTimeout(() => {
              blip.classList.remove('anim');
              void blip.offsetWidth;
              blip.classList.add('anim');
              setTimeout(() => blip.classList.remove('anim'), 700);
            }, 50);
          });
        }
      }

      const toast = document.querySelector('cart-toast');
      const detailItems = event.detail?.items;

      if (!detailItems || !toast) return;

      const latestDetail = Array.isArray(detailItems) && detailItems.length ? detailItems[detailItems.length - 1] : null;
      const fromPdp = document.documentElement.classList.contains('template-product');
      const currentQuantity = Number(latestDetail?.quantity || 0);
      const previousQuantity = Number(latestDetail?.previousQuantity || 0);
      const isIncrease = currentQuantity > previousQuantity;
      const isFirstTime = currentQuantity === 1 && previousQuantity < 1;

      const shouldShow = toast && latestDetail && isIncrease && (fromPdp || isFirstTime);
      if (shouldShow) {
        const increasedItems = detailItems.filter(
          (item) => Number(item?.quantity || 0) > Number(item?.previousQuantity || 0)
        );
        const labels = increasedItems
          .map((item) => item?.productTitle || item?.title)
          .filter(Boolean)
          .map((label) => `<strong>${label}</strong>`);
        const count = increasedItems.length;
        const isMultiple = count > 1;

        const latestLabel = latestDetail?.productTitle || latestDetail?.title || '';
        const productLabel = labels.length ? labels.join(', ') : latestLabel;

        const messageTemplate = isMultiple && toast.dataset.messageMultiple
          ? toast.dataset.messageMultiple
          : toast.dataset.message || '';
        let message = messageTemplate;
        if (isMultiple && message.includes('[count]')) {
          message = message.replace('[count]', String(count));
        } else if (productLabel && message.includes('[product]')) {
          message = message.replace('[product]', `<strong>${productLabel}</strong>`);
        }
        if (message) toast.show(message);
      }
    });

    // Free shipping check onload
    this.updateShippingNotification('cart-shipping-notification');

    // Animate progress when the cart drawer opens
    this.initDrawerProgressAnimation();

    // Clean up preorder attribute if needed
    this.checkAndRemovePreorderAttribute();
  }

  /**
   *  Debounced onChange event handler. Updates the cart when the user stops clicking quantity buttons.
   * @param {*} event
   */
  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  /**
   * Returns an array of sections to render when the cart is updated. Limit: 5 sections.
   * Does not make the request to the server, only returns the section names to render.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Array} Array of sections to render
   */
  getSectionItemsToRender() {
    return ['cart-items','cart-shipping-notification', 'cart-recommendations', 'cart-note', 'cart-footer'];
  }

  /**
   * This function is always called when the cart is updated.
   * @param {string} json
   */
  updateCartSections (newHTML) {
    this.getSectionItemsToRender().forEach((item) => {
      const elementToReplace = document.getElementById(item);

      if (!elementToReplace) return;

      // replace old with new
      elementToReplace.innerHTML = newHTML.getElementById(item).innerHTML;

      if (item === 'cart-shipping-notification') {
        this.updateShippingNotification('cart-shipping-notification');
      }
    });
  }

  /**
   * This function handles multi-currency support for the shipping notification bar.
   * This is called when item is added to cart or when the cart is updated.
   * @param {string} selector
   * @param {string*} json - Cart JSON
   */
  updateShippingNotification(selector) {
    const shippingNotificationWrapper = document.getElementById(selector);

    if (!shippingNotificationWrapper) {
      return;
    }

    const shippingNotification = shippingNotificationWrapper.querySelector('[data-free-shipping-notification]');

    let cartTotal = parseInt(shippingNotification.dataset.cartTotal, 10),
        freeShippingTreshold = parseInt(shippingNotification.dataset.threshold,10);

    const progress = Math.max(0, Math.min(100, Math.round((cartTotal / freeShippingTreshold) * 100)));
    const track = shippingNotificationWrapper.querySelector('.free-shipping-progress__track');
    if (track) {
      const finalProgress = `${progress}%`;
      const prevProgress = track.dataset.progress || track.style.getPropertyValue('--progress') || '0%';

      // Update ARIA regardless
      track.setAttribute('aria-valuenow', String(progress));

      if (prevProgress === finalProgress) {
        track.style.setProperty('--progress', finalProgress);
      } else {
        // Set previous value first, force reflow, then set final to trigger CSS transition
        track.style.setProperty('--progress', prevProgress);
        // Force reflow to ensure the browser applies the previous value before transitioning
        void track.offsetWidth;
        requestAnimationFrame(() => {
          track.style.setProperty('--progress', finalProgress);
        });
      }

      track.dataset.progress = finalProgress;
    }

    if (cartTotal > freeShippingTreshold) {
      shippingNotification.classList.add('threshold-reached')
      shippingNotification.querySelector('span').innerHTML = window.cartStrings.shipping_free;
    } else {
      let missingAmount = freeShippingTreshold - cartTotal,
          message = window.cartStrings.shipping_not_free.replace('[amount]', Woolman.Utils.formatMoney(missingAmount, null, true));

      shippingNotification.classList.remove('threshold-reached')
      shippingNotification.querySelector('span').innerHTML = message;
    }
  }

  /**
   * Animates the free shipping progress from 0% when the cart drawer opens.
   */
  initDrawerProgressAnimation() {
    const drawer = document.getElementById('drawer-cart');
    if (!drawer) return;

    let wasOpen = drawer.classList.contains('is-open');

    const observer = new MutationObserver(() => {
      const nowOpen = drawer.classList.contains('is-open');
      const wrapper = document.getElementById('cart-shipping-notification');
      if (!wrapper) { wasOpen = nowOpen; return; }
      const track = wrapper.querySelector('.free-shipping-progress__track');
      if (!track) { wasOpen = nowOpen; return; }

      // Drawer just opened → animate 0% → current
      if (nowOpen && !wasOpen) {
        track.dataset.progress = '0%';
        track.style.setProperty('--progress', '0%');
        void track.offsetWidth;
        requestAnimationFrame(() => {
          this.updateShippingNotification('cart-shipping-notification');
        });
      }

      // Drawer just closed → animate current → 0%
      if (!nowOpen && wasOpen) {
        const current = track.dataset.progress || track.style.getPropertyValue('--progress') || '0%';
        track.style.setProperty('--progress', current);
        void track.offsetWidth;
        requestAnimationFrame(() => {
          track.style.setProperty('--progress', '0%');
          track.setAttribute('aria-valuenow', '0');
          track.dataset.progress = '0%';
        });
      }

      wasOpen = nowOpen;
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Fetches fresh sections from the server and updates the cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderItems() {
    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateCartSections(html);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Fetches fresh sections from the server and updates the cart. This is called when item is added to cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderAndShowItems() {
    const parentDrawer = this.closest('[data-drawer]');

    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateCartSections(html);

        if (!parentDrawer) return;
        Woolman.ModalsAndDrawers.showModalOrDrawer(parentDrawer.getAttribute('id'))
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Updates the quantity of a line item in the cart. This is called when the user changes the quantity of an item in the cart.
   * @param {*} line
   * @param {*} quantity
   * @param {*} name
   */
  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    fetch(`${routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: 'main-cart',
        sections_url: window.location.pathname,
      }),
    })
    .then((response) => response.json())
    .then((responseJson) => {

      const quantityElement = document.getElementById(`Quantity-${line}`);
      const items = document.querySelectorAll('.cart-item');

      if (responseJson.errors) {
        quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, responseJson.errors);
        return;
      }

      const event = new CustomEvent('cart:update', { detail: responseJson,});
      document.dispatchEvent(event);

      // Render new items according to response
      const html = new DOMParser().parseFromString(responseJson.sections['main-cart'], 'text/html');
      this.updateCartSections(html);

      // Clean up preorder attribute if needed
      this.checkAndRemovePreorderAttribute();

      const newLineItem = document.getElementById(`CartItem-${line}`);
      if (newLineItem && newLineItem.querySelector(`[name="${name}"]`)) newLineItem.querySelector(`[name="${name}"]`).focus();
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      this.disableLoading();
      this.querySelectorAll('.quantity.is-updating').forEach((node) => node.classList.remove('is-updating'));
    });
  }

  /**
   * Updates the live regions for the cart.
   * @param {*} line
   * @param {*} message
   */
  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      lineItemError.classList.remove('hide');
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    this.classList.add('is-processing');
    const lineSelector = `#CartItem-${line}`;
    this.querySelectorAll(`${lineSelector} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    this.querySelectorAll(`${lineSelector} quantity-input`).forEach((quantityInput) => quantityInput.classList.add('is-loading'));
    document.activeElement.blur();
  }

  disableLoading() {
    this.classList.remove('is-processing');
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
    this.querySelectorAll('quantity-input.is-loading').forEach((quantityInput) => quantityInput.classList.remove('is-loading'));
  }

  /**
   * Checks if there are any preorder products in the cart.
   * If not, removes the _preorder attribute from cart attributes.
   * Optimized to do both check and removal in a single function.
   */
  async checkAndRemovePreorderAttribute() {
    try {
      // Fetch current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (!cart) return;

      // If cart is empty or has no items, remove attribute if it exists
      if (!cart.items || cart.items.length === 0) {
        if (cart.attributes?._preorder) {
          await fetch(`${routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              attributes: { _preorder: '' }
            })
          });
        }
        return;
      }

      // Check if any item has _preorder property
      const hasPreorderProduct = cart.items.some(item => {
        if (!item.properties) return false;
        const preorderValue = item.properties._preorder;
        return preorderValue === 'true' || preorderValue === true;
      });

      // If no preorder products found but attribute exists, remove it
      if (!hasPreorderProduct && cart.attributes?._preorder) {
        await fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            attributes: { _preorder: '' }
          })
        });
      }
    } catch (error) {
      console.error('Error checking preorder attribute:', error);
    }
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
           'Content-Type': 'application/json',
            Accept: `application/json`,
          },
          ...{ body }
        });
      }, 300))
    }
  });
};

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loadRecommendations();
      document.addEventListener('ajaxProduct:added', (event) => this.loadRecommendations(event));
      document.addEventListener('cart:update', (event) => this.loadRecommendations(event));
    }

    loadRecommendations(event) {
      let url = this.dataset.url;
      if (!url) {
        this.innerHTML = '';
        return;
      }
      const productIdMissing = !/product_id=\d+/.test(url);
      if (productIdMissing && event?.detail?.items?.[0]) {
        const id = event.detail.items[0].productId || event.detail.items[0].product_id;
        if (id) url = url.replace(/product_id=[^&]*/, 'product_id=' + id);
      }
      if (productIdMissing && !/product_id=\d+/.test(url)) {
        this.innerHTML = '';
        return;
      }
      fetch(url)
        .then(response => response.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('.cart-recommendations');

          if (recommendations) {
            this.innerHTML = '';
            const fragment = document.createDocumentFragment();
            while (recommendations.firstChild) {
              fragment.appendChild(recommendations.firstChild);
            }
            this.appendChild(fragment);
          }
        })
        .catch(e => {
          console.error(e);
        });
    }
  });
}